import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { IoCreateOutline } from 'react-icons/io5';
import './CommonMaster.css';
import { showMessageBox } from '../site/fn';
import { getJavaApiBaseUrl } from '../../config/apiBase';
import { EmptyState, ErrorState, Skeleton } from '../../shared-ui/States';

const AUTH_TOKEN_KEY = 'zTaskAuthToken';

const getApiHeaders = () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) throw new Error('Authentication token not found. Please log in again.');
    return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Client': 'web',
    };
};

const LIST_PATH = '/organization/common-master';

/** Columns users manage as lookups (hide technical / system fields from primary view). */
const LOOKUP_COLUMNS = [
    'category',
    'priorityInDailyTask',
    'currentStatus',
    'vendorType',
    'currency',
    'sector',
    'unit',
    'revType',
    'componentFor',
    'invoiceFor',
    'assetType',
    'assetMake',
    'encl',
    'docType',
    'bankName',
    'monthName',
    'financialYear',
    'yearAbbr',
];

const HIDDEN_COLUMNS = new Set(['id', 'alertIdPwd', 'alertEmailId', 'alertIdSmtp', 'alertIdPort', 'alertSslType']);

const getRouteInfo = () => {
    const path = window.location.pathname;
    if (path.includes('/common-master/edit/')) {
        const id = path.split('/').pop();
        return { view: 'form', id };
    }
    if (path.includes('/common-master/new')) {
        return { view: 'form', id: null };
    }
    return { view: 'list', id: null };
};

const placeholders = {
    docType: 'e.g., IN',
    priorityInDailyTask: 'e.g., High, Medium, Low',
    category: 'e.g., Deployment',
    currentStatus: 'e.g., open',
    bankName: 'e.g., Central Bank',
    monthName: 'e.g., Feb',
    financialYear: 'e.g., 2025-2026',
    attachmentSize: 'e.g., 1024',
    unit: 'e.g., Hours',
    vendorType: 'e.g., Customer',
    sector: 'e.g., Infrastructure',
    assetType: 'e.g., CRAWLER',
    assetMake: 'e.g., Liebherr',
    alertEmailId: 'e.g., alerts@example.com',
    alertIdPwd: 'Enter password',
    alertIdSmtp: 'e.g., smtp.example.com',
    alertIdPort: 'e.g., 587',
    alertSslType: 'e.g., TLS',
    attachmentFileType: 'e.g., PDF, JPG',
    yearAbbr: 'e.g., 2526',
    currency: 'e.g., INR',
    revType: 'e.g., OT',
    componentFor: 'e.g., Crawler',
    invoiceFor: 'e.g., Hiring',
    encl: 'e.g., Completion Certificate',
};

const labelFor = (col) =>
    String(col)
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();

const CommonMaster = () => {
    const [route, setRoute] = useState(getRouteInfo());
    const [masters, setMasters] = useState([]);
    const [columns, setColumns] = useState([]);
    const [formData, setFormData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentColumn, setCurrentColumn] = useState(null);
    const [newValue, setNewValue] = useState('');
    const [viewMode, setViewMode] = useState('grouped'); // grouped | table

    const API_BASE_URL = getJavaApiBaseUrl();

    const navigate = (path) => {
        window.history.pushState({}, '', path);
        setRoute(getRouteInfo());
    };

    const loadList = useCallback(async () => {
        setIsLoading(true);
        setLoadError(null);
        try {
            if (!API_BASE_URL) throw new Error('API base URL is not defined.');
            const headers = getApiHeaders();

            const [columnsResponse, mastersResponse] = await Promise.all([
                fetch(`${API_BASE_URL}/api/get-common-master-columns`, { headers }),
                fetch(`${API_BASE_URL}/api/get-common-master`, { headers }),
            ]);

            if (columnsResponse.status === 401 || mastersResponse.status === 401) {
                throw new Error('Session expired. Please log in again.');
            }

            if (!columnsResponse.ok) {
                throw new Error(`Failed to fetch columns (HTTP ${columnsResponse.status})`);
            }
            const columnsData = await columnsResponse.json();
            const rawCols = Array.isArray(columnsData.columns) ? columnsData.columns : [];
            setColumns(rawCols.filter((c) => !HIDDEN_COLUMNS.has(c)));

            // Empty list is valid (legacy API returned 404)
            if (mastersResponse.status === 404) {
                setMasters([]);
                return;
            }
            if (!mastersResponse.ok) {
                const errBody = await mastersResponse.json().catch(() => ({}));
                throw new Error(errBody.message || `Failed to fetch common masters (HTTP ${mastersResponse.status})`);
            }
            const mastersData = await mastersResponse.json();
            const list = Array.isArray(mastersData.message)
                ? mastersData.message
                : Array.isArray(mastersData)
                  ? mastersData
                  : [];
            setMasters([...list].sort((a, b) => Number(b.id) - Number(a.id)));
        } catch (error) {
            console.error('Error fetching common master:', error);
            setLoadError(error.message || 'Failed to load common master');
            setMasters([]);
        } finally {
            setIsLoading(false);
        }
    }, [API_BASE_URL]);

    useEffect(() => {
        const fetchMasterById = async (id) => {
            setIsLoading(true);
            setLoadError(null);
            try {
                if (!API_BASE_URL) throw new Error('API base URL is not defined.');
                const response = await fetch(`${API_BASE_URL}/api/get-common-master-by-id?id=${id}`, {
                    headers: getApiHeaders(),
                });
                if (!response.ok) throw new Error(`Failed to fetch master with ID ${id}`);
                const data = await response.json();
                setFormData(data.message || {});
            } catch (error) {
                console.error('Error fetching master by ID:', error);
                setLoadError(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        const initializeNewMaster = () => {
            setFormData({
                docType: '',
                priorityInDailyTask: '',
                category: '',
                currentStatus: '',
                bankName: '',
                monthName: '',
                financialYear: '',
                attachmentSize: 0,
                unit: '',
                vendorType: '',
                sector: '',
                assetType: '',
                assetMake: '',
                alertEmailId: '',
                alertIdPwd: '',
                alertIdSmtp: '',
                alertIdPort: 0,
                alertSslType: '',
                attachmentFileType: '',
                fromDate: '',
                toDate: '',
                yearAbbr: '',
                active: true,
                currency: '',
                revType: '',
                componentFor: '',
                invoiceFor: '',
                encl: '',
            });
        };

        if (route.view === 'list') {
            loadList();
        } else if (route.view === 'form' && route.id) {
            fetchMasterById(route.id);
        } else {
            initializeNewMaster();
        }
    }, [route, API_BASE_URL, loadList]);

    const handleCreateClick = () => navigate(`${LIST_PATH}/new`);
    const handleEditClick = (master) => navigate(`${LIST_PATH}/edit/${master.id}`);
    const handleCancel = () => navigate(LIST_PATH);

    const handleAddEntryClick = (columnName) => {
        setCurrentColumn(columnName);
        setNewValue('');
        setIsModalOpen(true);
    };

    const handleModalSubmit = async () => {
        if (!currentColumn || !newValue.trim()) {
            showMessageBox('Please enter a value.', 'error');
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(
                `${API_BASE_URL}/api/edit-common-master-column?columnName=${encodeURIComponent(currentColumn)}&value=${encodeURIComponent(newValue)}`,
                { method: 'PATCH', headers: getApiHeaders() }
            );

            if (response.ok) {
                showMessageBox('New entry added successfully!', 'success');
                setIsModalOpen(false);
                await loadList();
            } else {
                const errorData = await response.json().catch(() => ({}));
                showMessageBox(`Failed to add entry: ${errorData.message || errorData.error || 'Unknown server error'}`, 'error');
            }
        } catch (error) {
            console.error('Error adding new entry:', error);
            showMessageBox(error.message || 'An error occurred while adding the new entry.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!API_BASE_URL) {
            showMessageBox('API configuration is missing.', 'info');
            return;
        }

        const isEditing = !!route.id;
        const url = isEditing
            ? `${API_BASE_URL}/api/edit-common-master-by-id?id=${route.id}`
            : `${API_BASE_URL}/api/edit-common-master`;

        const payload = { ...formData };
        if (payload.fromDate) payload.fromDate = new Date(payload.fromDate).toISOString();
        if (payload.toDate) payload.toDate = new Date(payload.toDate).toISOString();

        try {
            const response = await fetch(url, {
                method: 'PATCH',
                headers: getApiHeaders(),
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                showMessageBox(`Common master ${isEditing ? 'updated' : 'created'} successfully!`, 'success');
                navigate(LIST_PATH);
            } else {
                const errorData = await response.json().catch(() => ({}));
                showMessageBox(
                    `Failed to ${isEditing ? 'update' : 'create'} master: ${errorData.message || 'Unknown error'}`,
                    'error'
                );
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            showMessageBox(error.message || 'An error occurred while submitting the form.', 'error');
        }
    };

    const displayValue = (value) => {
        if (value === null || value === undefined || value === '') {
            return <span className="empty-value">—</span>;
        }
        return typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value);
    };

    const groupedLookups = useMemo(() => {
        const preferred = LOOKUP_COLUMNS.filter((c) => columns.includes(c));
        const rest = columns.filter((c) => !LOOKUP_COLUMNS.includes(c) && typeof masters[0]?.[c] !== 'boolean');
        const ordered = [...preferred, ...rest];

        return ordered
            .map((col) => {
                const values = masters
                    .map((m) => ({ id: m.id, value: m[col], active: m.active }))
                    .filter((x) => x.value !== null && x.value !== undefined && String(x.value).trim() !== '');
                return { column: col, label: labelFor(col), values };
            })
            .filter((g) => g.values.length > 0);
    }, [masters, columns]);

    if (route.view === 'list') {
        return (
            <div className="common-master-container">
                <div className="top-bar">
                    <h1>Common Master</h1>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            className={`create-btn ${viewMode === 'grouped' ? '' : 'create-btn--ghost'}`}
                            style={viewMode !== 'grouped' ? { background: '#64748b' } : undefined}
                            onClick={() => setViewMode('grouped')}
                        >
                            By field
                        </button>
                        <button
                            type="button"
                            className="create-btn"
                            style={viewMode !== 'table' ? { background: '#64748b' } : undefined}
                            onClick={() => setViewMode('table')}
                        >
                            Full table
                        </button>
                        <button className="create-btn" onClick={handleCreateClick}>
                            Create / Edit Full Record
                        </button>
                    </div>
                </div>

                {isLoading && <Skeleton rows={6} height={36} />}

                {!isLoading && loadError && (
                    <ErrorState title="Could not load Common Master" description={loadError} onRetry={loadList} />
                )}

                {!isLoading && !loadError && masters.length === 0 && (
                    <EmptyState
                        title="No common master records"
                        description="Lookup values for tasks, customers, and work orders will appear here once added."
                        action={
                            <button type="button" className="create-btn" onClick={handleCreateClick}>
                                Create record
                            </button>
                        }
                    />
                )}

                {!isLoading && !loadError && masters.length > 0 && viewMode === 'grouped' && (
                    <div className="cm-grouped">
                        <p className="cm-grouped__hint">
                            Showing {masters.length} record(s) across {groupedLookups.length} field(s). Use “+ Add” to append a value.
                        </p>
                        {groupedLookups.length === 0 ? (
                            <EmptyState
                                title="No lookup values found"
                                description="Records exist but have no displayable field values."
                            />
                        ) : (
                            groupedLookups.map((group) => (
                                <section key={group.column} className="cm-group-card">
                                    <header className="cm-group-card__head">
                                        <h2>{group.label}</h2>
                                        <button
                                            type="button"
                                            className="add-entry-btn"
                                            onClick={() => handleAddEntryClick(group.column)}
                                            title={`Add new ${group.label}`}
                                        >
                                            + Add
                                        </button>
                                    </header>
                                    <ul className="cm-chip-list">
                                        {group.values.map((item) => (
                                            <li key={`${group.column}-${item.id}`} className="cm-chip">
                                                <span>{String(item.value)}</span>
                                                <button
                                                    type="button"
                                                    className="icon-btn icon-btn-edit"
                                                    title="Edit full record"
                                                    onClick={() => handleEditClick({ id: item.id })}
                                                >
                                                    <IoCreateOutline size={16} />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            ))
                        )}
                    </div>
                )}

                {!isLoading && !loadError && masters.length > 0 && viewMode === 'table' && (
                    <div className="table-responsive">
                        <table className="master-table">
                            <thead>
                                <tr>
                                    {columns.map((col) => (
                                        <th key={col}>
                                            {labelFor(col)}
                                            <button
                                                className="add-entry-btn"
                                                onClick={() => handleAddEntryClick(col)}
                                                title={`Add new entry to ${col}`}
                                            >
                                                +
                                            </button>
                                        </th>
                                    ))}
                                    <th className="actions-col-head">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {masters.map((master) => (
                                    <tr key={master.id}>
                                        {columns.map((col) => (
                                            <td key={`${master.id}-${col}`}>{displayValue(master[col])}</td>
                                        ))}
                                        <td className="actions-cell">
                                            <button
                                                type="button"
                                                className="icon-btn icon-btn-edit"
                                                title="Edit"
                                                onClick={() => handleEditClick(master)}
                                            >
                                                <IoCreateOutline size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {isModalOpen && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <h2>Add New Entry for &apos;{currentColumn}&apos;</h2>
                            <input
                                type="text"
                                className="modal-input"
                                value={newValue}
                                onChange={(e) => setNewValue(e.target.value)}
                                placeholder={`Enter new value for ${currentColumn}`}
                                autoFocus
                            />
                            <div className="modal-actions">
                                <button type="button" className="cancel-btn" onClick={() => setIsModalOpen(false)}>
                                    Cancel
                                </button>
                                <button type="button" className="save-btn" onClick={handleModalSubmit}>
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="form-container">
            <div className="form-header">
                <h1>{route.id ? 'Edit Common Master' : 'Create Common Master'}</h1>
            </div>
            {isLoading && route.id ? (
                <div>Loading form data...</div>
            ) : (
                <form onSubmit={handleSubmit} className="full-page-form">
                    <div className="form-grid">
                        {Object.keys(formData).map((key) => {
                            const isDate = key === 'fromDate' || key === 'toDate';
                            const value = isDate && formData[key] ? String(formData[key]).split('T')[0] : formData[key];

                            return (
                                <div className="form-group" key={key}>
                                    <label htmlFor={key}>{labelFor(key)}</label>
                                    <input
                                        type={isDate ? 'date' : typeof formData[key] === 'boolean' ? 'checkbox' : 'text'}
                                        id={key}
                                        name={key}
                                        placeholder={placeholders[key] || `Enter ${key}`}
                                        checked={typeof formData[key] === 'boolean' ? formData[key] : undefined}
                                        value={typeof formData[key] !== 'boolean' ? value || '' : ''}
                                        onChange={handleInputChange}
                                        className={typeof formData[key] === 'boolean' ? 'form-checkbox' : 'form-input'}
                                        disabled={key === 'id'}
                                    />
                                </div>
                            );
                        })}
                    </div>
                    <div className="form-footer">
                        <button type="button" className="cancel-btn" onClick={handleCancel}>
                            Cancel
                        </button>
                        <button type="submit" className="save-btn">
                            {route.id ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default CommonMaster;
